import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sprout, Volume2, VolumeX } from "lucide-react";
import { playPageFlip } from "@/lib/pageFlipSound";
import ZoomImage from "@/components/ZoomImage";
import FlowerButton from "@/components/FlowerButton";
import { PRODUCTS, fmtPrice } from "@/data/mellow";
import { useCart } from "@/context/CartContext";

const product = PRODUCTS[0];
const pages = [{ title: "Couverture", img: product.img, cover: true }, ...product.themes];

const ComingSoon = ({ title }) => (
  <div
    className="w-full aspect-[1190/1683] rounded-[1.75rem] border-2 border-dashed border-[#C8D0B7] bg-[#EEF1E4] flex flex-col items-center justify-center text-center p-8"
    data-testid="theme-coming-soon"
  >
    <Sprout className="w-10 h-10 text-[#8A9A6E] mb-4 animate-float-slow" />
    <p className="font-cursive text-4xl text-[#78353A] leading-tight">{title}</p>
    <p className="mt-3 font-cursive text-2xl text-[#78353A]/60">Ce thème pousse encore… aperçu bientôt ✿</p>
  </div>
);

export default function Feuilleter() {
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(0);
  const [sound, setSound] = useState(() => localStorage.getItem("mm_flip_sound") !== "off");
  const { add } = useCart();
  const page = pages[i];
  const turnTo = (n) => {
    const next = (n + pages.length) % pages.length;
    if (next === i) return;
    if (sound) playPageFlip();
    setFlip((f) => f + 1);
    setI(next);
  };
  const go = (n) => turnTo(n);
  const toggleSound = () => {
    const v = !sound;
    setSound(v);
    localStorage.setItem("mm_flip_sound", v ? "on" : "off");
    if (v) playPageFlip();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 pb-28 md:pb-12">
      <p className="font-cursive text-2xl text-[#78353A]/70">Green Days</p>
      <h1 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none" data-testid="feuilleter-title">Feuilleter le carnet</h1>
      <p className="mt-4 text-[#4A3B32]/85 max-w-2xl leading-relaxed">
        Huit thèmes, huit petites histoires du jardin. Chaque thème s'ouvre sur une page illustrée qui raconte ce qui se passe sur l'image, suivie de cinq pages vides à remplir, à détacher, à offrir.
      </p>

      <div className="mt-10 grid lg:grid-cols-[minmax(0,460px)_1fr] gap-10 items-start">
        {/* Page viewer */}
        <div className="relative mx-auto w-full max-w-[460px]">
          <div className="absolute -inset-4 blob-sage opacity-60 blur-2xl pointer-events-none" aria-hidden="true"></div>
          <div className="relative page-turn" key={flip}>
            {page.img ? (
              <ZoomImage
                src={page.img}
                alt={page.title}
                className="w-full rounded-[1.75rem] shadow-[0_20px_60px_rgb(0,0,0,0.12)]"
                data-testid="feuilleter-page-img"
              />
            ) : (
              <ComingSoon title={page.title} />
            )}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <button type="button" onClick={() => go(i - 1)} data-testid="feuilleter-prev"
              className="w-11 h-11 rounded-full bg-[#FAF9F6] border border-[#D1D5CB] flex items-center justify-center text-[#78353A] hover:bg-[#EAECE1] transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <span className="font-cursive text-2xl text-[#78353A]" data-testid="feuilleter-counter">{i === 0 ? "Couverture" : `Thème ${i} / ${product.themes.length}`}</span>
              <button type="button" onClick={toggleSound} data-testid="feuilleter-sound-toggle" aria-pressed={sound}
                title={sound ? "Couper le bruit de page" : "Activer le bruit de page"}
                className="w-9 h-9 rounded-full bg-[#FAF9F6] border border-[#D1D5CB] flex items-center justify-center text-[#78353A] hover:bg-[#EAECE1] transition-colors">
                {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            </div>
            <button type="button" onClick={() => go(i + 1)} data-testid="feuilleter-next"
              className="w-11 h-11 rounded-full bg-[#FAF9F6] border border-[#D1D5CB] flex items-center justify-center text-[#78353A] hover:bg-[#EAECE1] transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Text side */}
        <div>
          <h2 className="font-cursive text-4xl md:text-5xl text-[#78353A] leading-none" data-testid="feuilleter-page-title">{page.title}</h2>
          {page.cover && (
            <p className="mt-4 text-[#4A3B32]/85 leading-relaxed max-w-xl">{product.desc}</p>
          )}
          {page.story && (
            <div className="mt-5 space-y-4 max-w-xl" data-testid="feuilleter-story">
              {page.story.map((s, k) => (
                <p key={k} className="text-[#4A3B32]/90 leading-relaxed">{s}</p>
              ))}
            </div>
          )}
          {page.note && (
            <p className="mt-5 inline-block rotate-[-1.5deg] rounded-2xl bg-[#FFF2B2] px-5 py-3 font-cursive text-2xl text-[#78353A] shadow-[0_8px_20px_-10px_rgba(74,59,50,0.4)]" data-testid="feuilleter-note">
              {page.note}
            </p>
          )}
          {page.checklist && (
            <div className="mt-6 rounded-3xl border-2 border-dashed border-[#E9C46A] bg-[#FFF8E8] p-6 max-w-md" data-testid="feuilleter-checklist">
              <h3 className="font-cursive text-3xl text-[#78353A] mb-3">{page.checklistTitle}</h3>
              <ul className="space-y-2">
                {page.checklist.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-[#4A3B32]/90">
                    <span className="mt-1 w-4 h-4 rounded-full border-2 border-[#78353A]/40 shrink-0" aria-hidden="true"></span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!page.cover && !page.story && (
            <p className="mt-4 font-cursive text-2xl text-[#78353A]/70">La petite histoire de ce thème arrive bientôt.</p>
          )}

          {/* Theme index */}
          <ol className="mt-8 flex flex-wrap gap-2" data-testid="feuilleter-index">
            {pages.map((p, k) => (
              <li key={p.title}>
                <button type="button" onClick={() => turnTo(k)} data-testid={`feuilleter-tab-${k}`}
                  className={`text-sm px-3 py-1 rounded-full border transition-colors ${k === i ? "bg-[#78353A] text-[#FAF9F6] border-[#78353A]" : "bg-[#FAF9F6] text-[#4A3B32] border-[#D1D5CB] hover:bg-[#EAECE1]"}`}>
                  {k === 0 ? "Couverture" : `${k}. ${p.title}`}
                </button>
              </li>
            ))}
          </ol>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <span className="font-cursive text-5xl text-[#4A3B32] leading-none">{fmtPrice(product.price)}</span>
            <FlowerButton variant="pink" data-testid="feuilleter-add-to-cart"
              onClick={() => add(product.lookup_key)}>
              Ajouter au panier
            </FlowerButton>
            <Link to="/boutique" className="font-cursive text-2xl text-[#78353A] underline decoration-dotted underline-offset-4">Retour à la boutique</Link>
          </div>
        </div>
      </div>
      <div className="md:hidden fixed bottom-4 inset-x-4 z-40 flex items-center justify-between rounded-full bg-[#FFFDF9]/95 backdrop-blur border-2 border-dashed border-[#D1D5CB] shadow-lg px-2 py-2" data-testid="feuilleter-mobile-nav">
        <button type="button" onClick={() => go(i - 1)} aria-label="Page précédente" data-testid="feuilleter-mobile-prev" className="w-11 h-11 rounded-full bg-[#EAECE1] flex items-center justify-center text-[#78353A]"><ChevronLeft className="w-5 h-5" /></button>
        <span className="font-cursive text-xl text-[#78353A]">{i === 0 ? "Couverture" : `Thème ${i} / ${product.themes.length}`}</span>
        <button type="button" onClick={() => go(i + 1)} aria-label="Page suivante" data-testid="feuilleter-mobile-next" className="w-11 h-11 rounded-full bg-[#EAECE1] flex items-center justify-center text-[#78353A]"><ChevronRight className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
